import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Box, MenuItem, Select, Typography } from "@mui/material";
import Schedule from '../Filters/Schedule';

const CmLabel = (props) => {
    const { children, ...lProps } = props;
    return (
        <label style={{ color: '#6D7280', fontSize: '14px' }} {...lProps}>{children}</label>
    );
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

// Step1 now accepts travelCompanion and languages as props. It supports both legacy
// string arrays and the API object arrays described in the task. It also updates
// its internal state when props change.
const Step1 = ({ travelCompanion = [], languages = [], onSelectionChange, initialAssistant = '', initialLanguage = '', modalOpen = false, currentAssistant = null, currentLanguage = null, availabilityHours = [], selectedHour = null, currentSelectedHour = null }) => {
    const { t } = useTranslation("checkout");
    // Normalize incoming travelCompanion to an array of { id, label, name_en }
    const companionOptions = useMemo(() => {
        if (!Array.isArray(travelCompanion)) return [];
        return travelCompanion.map(item => {
            if (typeof item === 'string') {
                return { id: String(item), label: item, name_en: item };
            }
            // API object shape - ensure id is a string
            const id = item.ID ?? item.id ?? item.name ?? item.label ?? '';
            const label = item.name ?? item.label ?? item.name_en ?? String(id);
            return {
                id: String(id),
                label,
                // name_en: item.name_en ?? label
            };
        });
    }, [travelCompanion]);

    // Normalize languages to { code, label }
    const languageOptions = useMemo(() => {
        if (!Array.isArray(languages)) return [];
        return languages.map(item => {
            if (typeof item === 'string') {
                return { code: String(item), label: item };
            }
            const code = item.code ?? item.language ?? item.lang ?? '';
            const label = item.language ?? item.label ?? String(code);
            return { code: String(code), label };
        });
    }, [languages]);

    // Default selections: first available option key (id/code) or empty string
    const defaultAssistant = companionOptions.length ? companionOptions[0].id : '';
    const defaultLanguage = languageOptions.length ? languageOptions[0].code : '';

    // Start empty and apply initial/default once to avoid toggling
    const [assistantKey, setAssistantKey] = useState('');
    const [languageKey, setLanguageKey] = useState('');
    const [selectedHourKey, setSelectedHourKey] = useState('');

    // Refs to ensure we only apply initial/default assignment once per mount/options change
    const assistantInitRef = useRef(false);
    const languageInitRef = useRef(false);
    const lastSelectionPayloadRef = useRef('');
    const scheduleTouchedRef = useRef(false);

    // Apply initialAssistant or defaultAssistant once when options arrive. If already set and becomes invalid,
    // fall back to default but avoid flipping between default and saved values.
    useEffect(() => {
        if (!companionOptions.length) return;

        // First-time init: prefer initialAssistant (id or label), otherwise default
        if (!assistantInitRef.current) {
            if (initialAssistant) {
                const byId = companionOptions.find(o => o.id === String(initialAssistant));
                if (byId) {
                    setAssistantKey(byId.id);
                    assistantInitRef.current = true;
                    return;
                }
                const byLabel = companionOptions.find(o => o.label === initialAssistant);
                if (byLabel) {
                    setAssistantKey(byLabel.id);
                    assistantInitRef.current = true;
                    return;
                }
            }
            // No initial provided or not found — set default only if no selection yet
            setAssistantKey(prev => prev || defaultAssistant);
            assistantInitRef.current = true;
            return;
        }

        // Already initialized: ensure current selection still exists, otherwise fall back to default
        setAssistantKey(prev => {
            const exists = companionOptions.some(o => o.id === prev);
            if (exists) return prev;
            return defaultAssistant;
        });
    }, [companionOptions, initialAssistant, defaultAssistant]);

    useEffect(() => {
        if (!languageOptions.length) return;

        if (!languageInitRef.current) {
            if (initialLanguage) {
                const byCode = languageOptions.find(o => o.code === String(initialLanguage));
                if (byCode) {
                    setLanguageKey(byCode.code);
                    languageInitRef.current = true;
                    return;
                }
                const byLabel = languageOptions.find(o => o.label === initialLanguage);
                if (byLabel) {
                    setLanguageKey(byLabel.code);
                    languageInitRef.current = true;
                    return;
                }
            }
            setLanguageKey(prev => prev || defaultLanguage);
            languageInitRef.current = true;
            return;
        }

        setLanguageKey(prev => {
            const exists = languageOptions.some(o => o.code === prev);
            if (exists) return prev;
            return defaultLanguage;
        });
    }, [languageOptions, initialLanguage, defaultLanguage]);

    // When modal/dialog opens or parent current selections change, reapply initial values so UI matches saved cart/options
    useEffect(() => {
        if (!modalOpen) return;
        const assistantToUse = currentAssistant || initialAssistant;
        if (assistantToUse) {
            const byId = companionOptions.find(o => o.id === String(assistantToUse));
            if (byId) { setAssistantKey(byId.id); }
            else {
                const byLabel = companionOptions.find(o => o.label === assistantToUse);
                if (byLabel) { setAssistantKey(byLabel.id); }
            }
        } else {
            setAssistantKey(prev => prev || (companionOptions[0]?.id || ''));
        }

        const languageToUse = currentLanguage || initialLanguage;
        if (languageToUse) {
            const byCode = languageOptions.find(o => o.code === String(languageToUse));
            if (byCode) { setLanguageKey(byCode.code); }
            else {
                const byLabel = languageOptions.find(o => o.label === languageToUse);
                if (byLabel) { setLanguageKey(byLabel.code); }
            }
        } else {
            setLanguageKey(prev => prev || (languageOptions[0]?.code || ''));
        }
    }, [modalOpen, currentAssistant, currentLanguage]);

    // Notify parent of selection changes (provide both label and code/id when available)
    useEffect(() => {
        if (typeof onSelectionChange !== 'function') return;

        const langObj = languageOptions.find(o => o.code === languageKey) || { code: '', label: '' };
        const companionObj = companionOptions.find(o => o.id === assistantKey) || { id: '', label: '', name_en: '' };

        const payload = {};

        if (assistantKey) {
            payload.travelCompanion = {
                id: companionObj.id,
                label: companionObj.label,
                name_en: companionObj.name_en
            };
        }

        if (languageKey) {
            payload.language = {
                code: langObj.code,
                label: langObj.label
            };
        }

        // Emit schedule only after user interaction to avoid init loops (default vs saved hour).
        if (selectedHourKey && scheduleTouchedRef.current) {
            payload.schedule = { selectedHour: selectedHourKey };
        }

        // Avoid emitting empty payloads (prevents parent update loops).
        if (Object.keys(payload).length === 0) return;

        const payloadFingerprint = JSON.stringify(payload);
        if (lastSelectionPayloadRef.current === payloadFingerprint) return;
        lastSelectionPayloadRef.current = payloadFingerprint;

        onSelectionChange(payload);
    }, [assistantKey, languageKey, selectedHourKey, onSelectionChange, companionOptions, languageOptions]);

    // Schedule handling: helpers and local state
    const hourLabel = (hour) => {
        if (!hour) return '';
        if (typeof hour === 'string') return hour;
        return hour.hour || hour.time || '';
    }

    const firstAvailabilityLabel = (availabilityHours) => {
        if (!Array.isArray(availabilityHours)) return '';
        const container = Array.isArray(availabilityHours[0]) ? availabilityHours[0] : availabilityHours;
        return hourLabel(container && container.length ? container[0] : null);
    }

    // Initialize schedule selection when modal opens or props change
    useEffect(() => {
        if (selectedHour) {
            setSelectedHourKey(selectedHour);
            return;
        }
        const derived = firstAvailabilityLabel(availabilityHours);
        setSelectedHourKey(prev => prev || derived || '');
    }, [availabilityHours, selectedHour, modalOpen]);

    useEffect(() => {
        if (!modalOpen) {
            scheduleTouchedRef.current = false;
        }
    }, [modalOpen]);

    const handleChangeAssitants = (event) => {
        setAssistantKey(event.target.value);
    }

    const handleChangeLanguages = (event) => {
        setLanguageKey(event.target.value);
    }

    return (
        <>
            <Typography
                variant="h5"
                sx={{
                    fontSize: '20px',
                    color: 'secondary.main'
                }}
            >
                {t('update-tour-step1-about-you')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    width: '100%',
                    py: '5px'
                }}
            >
                <CmLabel htmlFor="assistant">{t('update-tour-step1-travel-with')}</CmLabel>
                <Select
                    labelId="assistant-id"
                    name="assistant"
                    value={assistantKey}
                    onChange={handleChangeAssitants}
                    MenuProps={MenuProps}
                    sx={{
                        fontSize: '13px',
                        height: '40px',
                        borderRadius: '8px'
                    }}
                >
                    {companionOptions.map((item) => (
                        <MenuItem
                            key={item.id ?? item.label}
                            value={item.id}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    width: '100%',
                    py: '5px'
                }}
            >
                <CmLabel htmlFor="language">{t('update-tour-step1-language-question')}</CmLabel>
                <Select
                    labelId="language-id"
                    name="language"
                    value={languageKey}
                    onChange={handleChangeLanguages}
                    MenuProps={MenuProps}
                    sx={{
                        fontSize: '13px',
                        height: '40px',
                        borderRadius: '8px'
                    }}
                >
                    {languageOptions.map((item) => (
                        <MenuItem
                            key={item.code ?? item.label}
                            value={item.code}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            <Box sx={{ mt: 2 }}>
                <CmLabel htmlFor="schedule">{t('update-tour-step1-schedule')}</CmLabel>
                <Schedule
                    title={t('update-tour-step1-schedule')}
                    availabilityHours={availabilityHours}
                    selectedHour={selectedHourKey}
                    onChangeSelectedHour={(val) => {
                        scheduleTouchedRef.current = true;
                        setSelectedHourKey(val);
                    }}
                />
            </Box>
        </>
    )
}

export default Step1;
